import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, RefreshCw, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import apiService from '../services/api';

const ConnectionStatus: React.FC = () => {
  const [isOnline, setIsOnline] = useState(true);
  const [isElectron, setIsElectron] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    // Check if running in Electron
    setIsElectron(apiService.isElectronApp());
    
    // Set initial connection status
    setIsOnline(apiService.getConnectionStatus());

    // Listen for connection status changes
    const handleConnectionChange = (event: CustomEvent) => {
      setIsOnline(event.detail.isOnline);
    };

    window.addEventListener('connectionStatusChange', handleConnectionChange as EventListener);

    return () => {
      window.removeEventListener('connectionStatusChange', handleConnectionChange as EventListener);
    };
  }, []);

  const handleSync = async () => {
    if (!isElectron) return;

    setIsSyncing(true);
    try {
      const result = await apiService.syncData();
      if (result?.success) {
        setLastSync(new Date());
        // Show success message
        console.log('Sync completed successfully');
      } else {
        console.error('Sync failed:', result?.error);
      }
    } catch (error) {
      console.error('Sync error:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  if (!isElectron) {
    return null; // Don't show in web version
  }

  return (
    <div className="flex items-center space-x-2 text-sm">
      {/* Connection Status */}
      <div className="flex items-center space-x-1">
        {isOnline ? (
          <Wifi className="h-4 w-4 text-green-500" />
        ) : (
          <WifiOff className="h-4 w-4 text-red-500" />
        )}
        <span className={isOnline ? 'text-green-600' : 'text-red-600'}>
          {isOnline ? 'Online' : 'Offline'}
        </span>
      </div>

      {/* Sync Button */}
      <button
        onClick={handleSync}
        disabled={isSyncing || !isOnline}
        className={`flex items-center space-x-1 px-2 py-1 rounded text-xs ${
          isSyncing || !isOnline
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
        }`}
        title={isOnline ? 'Sync data with server' : 'Sync unavailable offline'}
      >
        <RefreshCw className={`h-3 w-3 ${isSyncing ? 'animate-spin' : ''}`} />
        <span>{isSyncing ? 'Syncing...' : 'Sync'}</span>
      </button>

      {/* Last Sync Time */}
      {lastSync && (
        <div className="text-xs text-gray-500">
          Last sync: {lastSync.toLocaleTimeString()}
        </div>
      )}

      {/* Offline Warning */}
      {!isOnline && (
        <div className="flex items-center space-x-1 text-amber-600">
          <AlertCircle className="h-4 w-4" />
          <span className="text-xs">Working offline</span>
        </div>
      )}
    </div>
  );
};

export default ConnectionStatus;
